import { useEffect, useState, useCallback } from "react";
import {
  Plus, PencilSimple, Trash, Megaphone, Sparkle, WhatsappLogo, EnvelopeSimple,
  ChatCircleText, InstagramLogo, FacebookLogo, Globe, Tag, Check,
} from "@phosphor-icons/react";
import api, { money, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import Modal, { Field, inputCls, PrimaryBtn, GhostBtn } from "@/components/Modal";
import { toast } from "sonner";

const CATEGORIES = ["Haircuts", "Hair Coloring", "Highlights", "Keratin Treatments", "Beard Services", "Spa Packages", "Massages", "Facials", "Waxing", "Nail Services", "Gel Nails", "Pedicures", "Eyelash Extensions", "Eyebrow Services", "Makeup", "Bridal Packages", "Membership Promotions", "Holiday Promotions", "Birthday Discounts", "Referral Promotions", "Happy Hour Promotions", "Package Deals", "Gift Cards", "Seasonal Campaigns"];
const DISCOUNT_TYPES = [{ v: "percentage", l: "Percentage %" }, { v: "fixed", l: "Fixed amount" }, { v: "bogo", l: "Buy One Get One" }, { v: "package", l: "Package price" }, { v: "bundle", l: "Bundle deal" }];
const SEGMENTS = [{ v: "all", l: "All customers" }, { v: "vip", l: "VIP only" }, { v: "new", l: "New customers" }, { v: "birthday", l: "Birthday" }];
const CHANNELS = [
  { v: "whatsapp", l: "WhatsApp", icon: WhatsappLogo },
  { v: "email", l: "Email", icon: EnvelopeSimple },
  { v: "sms", l: "SMS", icon: ChatCircleText },
  { v: "instagram", l: "Instagram", icon: InstagramLogo },
  { v: "facebook", l: "Facebook", icon: FacebookLogo },
  { v: "website", l: "Website", icon: Globe },
];
const STATE_COLORS = { live: "#7A8B5C", scheduled: "#C9A227", expired: "#B86B5A", paused: "#6B6A67" };

const empty = { name: "", category: "Haircuts", description: "", discount_type: "percentage", discount_value: 10, code: "", customer_segment: "all", channels: ["whatsapp"], service_ids: [], staff_ids: [], starts_at: "", ends_at: "", active: true };

function discountLabel(p, currency, t) {
  if (p.discount_type === "percentage") return `${p.discount_value}${t.promo.offPct}`;
  if (p.discount_type === "fixed") return `${money(p.discount_value, currency)} ${t.promo.offFixed}`;
  if (p.discount_type === "bogo") return t.promo.bogo;
  if (p.discount_type === "package") return `${t.promo.pkg} · ${money(p.discount_value, currency)}`;
  return t.promo.bundle;
}

export default function Promotions() {
  const { currency } = useAuth();
  const { t } = useI18n();
  const [promos, setPromos] = useState([]);
  const [dialog, setDialog] = useState({ open: false, id: null });
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [ideas, setIdeas] = useState([]);

  const load = useCallback(async () => {
    const { data } = await api.get("/promotions");
    setPromos(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = (preset) => { setForm(preset ? { ...empty, ...preset } : empty); setDialog({ open: true, id: null }); };
  const openEdit = (p) => { setForm({ ...empty, ...p }); setDialog({ open: true, id: p.id }); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleChannel = (v) => setForm((f) => ({ ...f, channels: f.channels.includes(v) ? f.channels.filter((c) => c !== v) : [...f.channels, v] }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name, category: form.category, description: form.description,
        discount_type: form.discount_type, discount_value: Number(form.discount_value) || 0,
        code: form.code, customer_segment: form.customer_segment, channels: form.channels,
        service_ids: [], staff_ids: [],
        starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
        ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
        active: form.active,
      };
      if (dialog.id) await api.put(`/promotions/${dialog.id}`, payload);
      else await api.post("/promotions", payload);
      toast.success(dialog.id ? t.promo.updated : t.promo.created);
      setDialog({ open: false, id: null });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to save"); } finally { setSaving(false); }
  };

  const del = async (id) => { await api.delete(`/promotions/${id}`); toast.success(t.promo.deleted); load(); };

  const runAi = async () => {
    setAiOpen(true);
    setAiLoading(true);
    setIdeas([]);
    try {
      const { data } = await api.post("/promotions/ai-suggest", { goal: "increase revenue and fill quiet days" });
      setIdeas(data.ideas || []);
      if (!data.ideas?.length) toast.error("AI returned no ideas — try again.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "AI error");
    } finally { setAiLoading(false); }
  };

  const applyIdea = (idea) => {
    setAiOpen(false);
    openNew({
      name: idea.name || "", category: idea.category || "Haircuts", description: idea.description || "",
      discount_type: idea.discount_type || "percentage", discount_value: Number(idea.discount_value) || 10,
      channels: Array.isArray(idea.channels) && idea.channels.length ? idea.channels : ["whatsapp"],
    });
  };

  return (
    <div className="fade-up space-y-6" data-testid="promotions-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="label-caps">{t.promo.overline}</p>
          <h1 className="font-heading text-3xl font-light tracking-tight mt-1">{t.promo.title}</h1>
        </div>
        <div className="flex gap-2">
          <button data-testid="ai-suggest-btn" onClick={runAi}
            className="px-4 py-2.5 rounded-lg bg-white border border-[#E5E2DC] text-[#2C3E35] text-sm font-medium hover:bg-[#E8E4DF] transition-colors inline-flex items-center gap-2">
            <Sparkle size={16} weight="fill" className="text-[#B86B5A]" /> {t.promo.ai}
          </button>
          <button data-testid="new-promo-btn" onClick={() => openNew()}
            className="px-4 py-2.5 rounded-lg bg-[#2C3E35] text-white text-sm font-medium hover:bg-[#1E2B25] transition-colors inline-flex items-center gap-2">
            <Plus size={16} weight="bold" /> {t.promo.new}
          </button>
        </div>
      </div>

      {promos.length === 0 ? (
        <div className="bg-white rounded-xl border border-[#E5E2DC] py-16 text-center">
          <Megaphone size={40} weight="duotone" className="text-[#B86B5A] mx-auto" />
          <p className="font-heading text-lg mt-3">{t.promo.noneTitle}</p>
          <p className="text-sm text-[#6B6A67] mt-1">{t.promo.noneSub}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {promos.map((p) => (
            <div key={p.id} data-testid={`promo-card-${p.id}`} className="bg-white rounded-xl border border-[#E5E2DC] p-5 transition-transform duration-200 hover:-translate-y-1 hover:shadow-sm group">
              <div className="flex items-start justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full" style={{ background: `${STATE_COLORS[p.state]}18`, color: STATE_COLORS[p.state] }}>{t.promo[p.state]}</span>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button data-testid={`edit-promo-${p.id}`} onClick={() => openEdit(p)} className="p-1.5 rounded-lg text-[#6B6A67] hover:bg-[#E8E4DF]"><PencilSimple size={15} /></button>
                  <button data-testid={`del-promo-${p.id}`} onClick={() => del(p.id)} className="p-1.5 rounded-lg text-[#6B6A67] hover:bg-[#B86B5A]/12 hover:text-[#B86B5A]"><Trash size={15} /></button>
                </div>
              </div>
              <h3 className="font-heading text-lg font-medium mt-3">{p.name}</h3>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-[#6B6A67]">{p.category}</span>
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#B86B5A]"><Tag size={12} /> {discountLabel(p, currency, t)}</span>
              </div>
              {p.description && <p className="text-xs text-[#6B6A67] mt-2 leading-relaxed line-clamp-2">{p.description}</p>}
              <div className="mt-4 pt-3 border-t border-[#E5E2DC] flex items-center gap-2">
                {(p.channels || []).map((c) => {
                  const ch = CHANNELS.find((x) => x.v === c);
                  if (!ch) return null;
                  const Icon = ch.icon;
                  return <Icon key={c} size={16} weight="duotone" className="text-[#2C3E35]" title={ch.l} />;
                })}
                {p.code && <span className="ml-auto text-[11px] font-mono px-2 py-0.5 rounded bg-[#E8E4DF] text-[#2C3E35]">{p.code}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor */}
      <Modal open={dialog.open} onClose={() => setDialog({ open: false, id: null })} testid="promo-dialog" size="lg"
        title={dialog.id ? t.promo.editTitle : t.promo.newTitle}
        footer={<><GhostBtn onClick={() => setDialog({ open: false, id: null })}>{t.btn.cancel}</GhostBtn><PrimaryBtn onClick={save} disabled={saving} data-testid="promo-save">{saving ? t.btn.saving : t.promo.save}</PrimaryBtn></>}>
        <div className="space-y-4">
          <Field label={t.promo.fName}><input data-testid="promo-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Mid-week Color Refresh" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t.promo.fCategory}>
              <select data-testid="promo-category" className={inputCls} value={form.category} onChange={set("category")}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label={t.promo.fCode}><input data-testid="promo-code" className={inputCls} value={form.code} onChange={set("code")} placeholder="COLOR20" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t.promo.fType}>
              <select data-testid="promo-discount-type" className={inputCls} value={form.discount_type} onChange={set("discount_type")}>
                {DISCOUNT_TYPES.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
              </select>
            </Field>
            <Field label={t.promo.fValue}><input data-testid="promo-discount-value" type="number" className={inputCls} value={form.discount_value} onChange={set("discount_value")} disabled={form.discount_type === "bogo"} /></Field>
          </div>
          <Field label={t.promo.fDesc}><textarea data-testid="promo-description" className={inputCls} rows={2} value={form.description} onChange={set("description")} /></Field>
          <Field label={t.promo.fSegment}>
            <select data-testid="promo-segment" className={inputCls} value={form.customer_segment} onChange={set("customer_segment")}>
              <option value="all">{t.promo.segAll}</option>
              <option value="vip">{t.promo.segVip}</option>
              <option value="new">{t.promo.segNew}</option>
              <option value="birthday">{t.promo.segBday}</option>
            </select>
          </Field>
          <Field label={t.promo.fChannels}>
            <div className="flex flex-wrap gap-2">
              {CHANNELS.map((c) => {
                const Icon = c.icon;
                const on = form.channels.includes(c.v);
                return (
                  <button key={c.v} type="button" data-testid={`promo-channel-${c.v}`} onClick={() => toggleChannel(c.v)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${on ? "bg-[#2C3E35] text-white border-[#2C3E35]" : "bg-white border-[#E5E2DC] text-[#6B6A67] hover:bg-[#E8E4DF]"}`}>
                    {on ? <Check size={13} weight="bold" /> : <Icon size={14} />} {c.l}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-[#6B6A67] mt-1.5">{t.promo.channelsNote}</p>
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t.promo.fStarts}><input data-testid="promo-starts" type="date" className={inputCls} value={form.starts_at ? form.starts_at.slice(0, 10) : ""} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} /></Field>
            <Field label={t.promo.fEnds}><input data-testid="promo-ends" type="date" className={inputCls} value={form.ends_at ? form.ends_at.slice(0, 10) : ""} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} /></Field>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" data-testid="promo-active" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="h-4 w-4 accent-[#2C3E35]" />
            {t.promo.active}
          </label>
        </div>
      </Modal>

      {/* AI suggestions */}
      <Modal open={aiOpen} onClose={() => setAiOpen(false)} testid="ai-promo-dialog" size="lg"
        title={t.promo.aiTitle}
        footer={<GhostBtn onClick={() => setAiOpen(false)}>{t.promo.close}</GhostBtn>}>
        {aiLoading ? (
          <div className="py-10 text-center">
            <div className="h-8 w-8 rounded-full border-2 border-[#E5E2DC] border-t-[#2C3E35] animate-spin mx-auto" />
            <p className="text-sm text-[#6B6A67] mt-3">{t.promo.aiLoading}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {ideas.length === 0 && <p className="text-sm text-[#6B6A67] py-6 text-center">{t.promo.aiNone}</p>}
            {ideas.map((idea, i) => (
              <div key={i} data-testid={`ai-idea-${i}`} className="rounded-xl border border-[#E5E2DC] p-4 hover:border-[#B86B5A]/40 transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold text-sm">{idea.name}</div>
                    <div className="text-xs text-[#B86B5A] font-medium mt-0.5">{idea.category} · {idea.discount_type === "percentage" ? `${idea.discount_value}${t.promo.offPct}` : idea.discount_type === "fixed" ? money(idea.discount_value, currency) + " " + t.promo.offFixed : idea.discount_type}</div>
                    <p className="text-sm text-[#6B6A67] mt-1.5">{idea.description}</p>
                    {idea.reason && <p className="text-xs text-[#9a978f] mt-1 italic">{idea.reason}</p>}
                  </div>
                  <PrimaryBtn onClick={() => applyIdea(idea)} data-testid={`use-idea-${i}`} className="shrink-0">{t.promo.use}</PrimaryBtn>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}
